import { QuestionCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Button, Form, Input, message, Popconfirm, Select, Switch, Tooltip } from 'antd';
import FormField from 'components/FormField';
import { Formik, FormikConfig, FormikProps } from 'formik';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { useScenario, useShopList } from 'views/Marketing';
import { isNoRegionScenario, scenarioPermissionMap } from './config';
import Conflict from './TaskForm/Conflict';
import FilterAfterSaleField, {
  validate as filterAfterSaleValidate,
} from './TaskForm/FilterAfterSaleField';
import PraiseTriggerPointField, {
  validate as praiseTriggerPointValidate,
} from './TaskForm/PraiseTriggerPointField';
import StoreScopesField from './TaskForm/StoreScopesField';
import TimeoutReplyField from './TaskForm/TimeoutReplyField';
import WelcomeReplyField from './TaskForm/WelcomeReplyField';
import FilterRuleField from './TaskForm/FilterRuleField';
import {
  getFilterNegativeBuyerDefault,
  isFilterNegativeBuyerScenarioSupport,
} from './TaskForm/FilterNegativeBuyer';
import {
  disable1Platform,
  isFilterEnable1ScenarioType,
  isFilterEnable3ScenarioType,
} from './TaskForm/FilterConsultBuyer';
import InviteFollowerField from './TaskForm/InviteFollowerField';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

const SelectAllWrapper = styled.div`
  padding: 8px 8px 0;
`;

const UsedShop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > span {
    display: flex;
    align-items: center;
    overflow: hidden;
  }
  > span > span {
    flex: 1 0 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  > button {
    margin: 0 20px;
  }
  .anticon {
    flex: 0 0 auto;
    margin-left: 5px;
    cursor: help;
  }
`;

const DisableStore = styled.div`
  display: inline-block;
  padding: 2px 8px;
  margin-right: 8px;
  font-size: 12px;
  border-radius: 2px;
  background: #f7f9fa;
`;

const styles = {
  w300: css`
    width: 300px;
    z-index: 1051;
  `,
};

export interface TaskFormValues extends Omit<Marketing.Task, 'rounds' | 'id' | 'scenarioId'> {
  id?: Marketing.Task['id'];
}

type FV = TaskFormValues;

export interface TaskFormProps extends Util.PartialPartial<FormikConfig<FV>, 'initialValues'> {
  disabled?: boolean;
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  children: (form: ReactNode, props: FormikProps<FV>) => ReactNode;
}

const nameLimitLength = 30;

const createDefaultInitialValues = (
  scenarioType: Marketing.AllScenarioType,
  platform: General.PlatformUpperCase,
): FV => {
  const executeTime = platform === 'TOKOPEDIA' ? 12 : 1;

  return {
    name: '',
    enable: true,
    isDefault: false,
    stores: [],
    filterItem: {
      type: 'NO_FILTER',
      specificSPUs: [],
      specificSKUs: [],
      specificCategories: [],
    },
    filterAfterSale: {
      enable: scenarioType === 'ORDER_REMINDER',
      confirmDays: 90,
      outOfOrderItem: true,
    },
    praiseTriggerPoint:
      // eslint-disable-next-line no-nested-ternary
      scenarioType === 'PRAISE_REMINDER'
        ? isTiKTokUpperCase(platform)
          ? 'SIGNED'
          : 'CONFIRMED'
        : undefined,
    filterHasConversation: scenarioType !== 'CANCELLING_DELIVERY_FAIL',
    welcomeReplyTaskConfig: {
      coolDownHour: 24,
    },
    timeoutReplyTaskConfig: {
      coolDownHour: 6,
      executeTime,
      executeTimeUnit: '2',
    },
    inviteFollowerTaskConfig: {
      limitDay: 30,
      limitCount: 5,
    },
    regionStoreList: [],
    filterRule: {
      filterConsultBuyer: {
        filterWaitProcessConversation:
          isFilterEnable1ScenarioType(scenarioType) && platform !== disable1Platform,
        filterConsult: false,
        filterConsultAfterCreateOrder: isFilterEnable3ScenarioType(scenarioType),
        filterConsultDays: 1,
      },
      filterNegativeBuyer: isFilterNegativeBuyerScenarioSupport(scenarioType, platform)
        ? getFilterNegativeBuyerDefault(scenarioType)
        : false,
    },
  };
};

function TaskForm(props: TaskFormProps) {
  const {
    disabled = false,
    scenarioType,
    platform,
    region,
    initialValues = createDefaultInitialValues(scenarioType, platform),
    children,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const { item: scenario, fetch: fetchScenario } = useScenario();

  const { list: shopList = [], loading: shopListLoading, fetchList: fetchShopList } = useShopList();

  const idShopMap = useMemo(() => {
    return shopList.reduce<Partial<Record<Marketing.Shop['externalId'], Marketing.Shop>>>(
      (res, shop) => {
        const { externalId } = shop;

        res[externalId] = shop;

        return res;
      },
      {},
    );
  }, [shopList]);

  const { tasks = [] } = scenario || {};

  const refsCurrent = {
    fetchScenario,
    fetchShopList,
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { fetchScenario, fetchShopList, t },
    } = refs;

    fetchScenario({
      scenarioType,
      platform,
      region,
    }).catch(() => {
      message.error(t('marketing.获取场景信息失败，请稍后再试'));
    });

    fetchShopList({
      scenarioType,
      platform,
      region,
    }).catch(() => {
      message.error(t('marketing.获取店铺列表失败，请稍后再试'));
    });
  }, [scenarioType, platform, region]);

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      const { id, name } = values;

      if (!name) {
        errors.name = t('marketing.请输入任务名称');
      } else if (name.length > nameLimitLength) {
        errors.name = t('marketing.任务名称不得超过x个字', { x: nameLimitLength });
      } else if (tasks.some((task) => task.taskId !== id && task.taskName === name)) {
        errors.name = t('marketing.任务名称已存在');
      }

      if (scenarioType === 'ORDER_REMINDER') {
        filterAfterSaleValidate(errors, values, t);
      }

      if (scenarioType === 'PRAISE_REMINDER') {
        praiseTriggerPointValidate(errors, values, t);
      }

      return errors;
    },
    [tasks, scenarioType, t],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validate={validate}
      onReset={() => {
        fetchShopList({
          scenarioType,
          platform,
          region,
        }).catch(() => {
          message.error(t('marketing.获取店铺列表失败，请稍后再试'));
        });
      }}
      {...otherProps}
    >
      {(formProps) => {
        const { initialValues } = formProps;

        const { stores: initialStores } = initialValues;

        const nameField = (
          <FormField<FV['name']> name="name" required label={t('marketing.任务名称')}>
            {({ field }) => {
              return (
                <Input
                  disabled={disabled}
                  placeholder={t('marketing.请输入任务名称')}
                  showCount
                  maxLength={nameLimitLength}
                  {...field}
                  style={{ width: '580px' }}
                />
              );
            }}
          </FormField>
        );

        const storesField = (
          <FormField<FV['stores']> name="stores" required label={t('aide.应用店铺')}>
            {({ field: { value, onChange, ...others } }) => {
              return disabled ? (
                <DisableStore>{t('department.已选x店铺', { x: value.length })}</DisableStore>
              ) : (
                <Select
                  disabled={disabled}
                  mode="multiple"
                  allowClear
                  showArrow
                  autoClearSearchValue={false}
                  maxTagCount="responsive"
                  loading={shopListLoading}
                  placeholder={t('aide.请选择应用店铺')}
                  value={value.map((shop) => shop.storeId)}
                  onChange={(storeIdList = []) => {
                    onChange(
                      storeIdList.reduce<Marketing.TaskStore[]>((res, storeId) => {
                        const { [storeId]: store } = idShopMap;

                        if (!store) {
                          return res;
                        }

                        const { name: storeName, alias } = store;

                        res.push({ storeId, storeName, alias });

                        return res;
                      }, []),
                    );
                  }}
                  optionFilterProp="title"
                  dropdownRender={(menu) => {
                    return (
                      <>
                        {menu}
                        <SelectAllWrapper>
                          <Button
                            block
                            disabled={disabled}
                            onClick={() => {
                              const selectableShopList = shopList.reduce<FV['stores']>(
                                (res, shop) => {
                                  const {
                                    externalId: storeId,
                                    name: storeName,
                                    bindTask,
                                    alias,
                                  } = shop;

                                  const disabled =
                                    bindTask &&
                                    initialStores.every((shop) => shop.storeId !== storeId);

                                  if (!disabled) {
                                    res.push({
                                      storeId,
                                      storeName,
                                      alias,
                                    });
                                  }

                                  return res;
                                },
                                [],
                              );

                              onChange(selectableShopList);
                            }}
                          >
                            {t('marketing.选中全部可选店铺')}
                          </Button>
                        </SelectAllWrapper>
                      </>
                    );
                  }}
                  {...others}
                  style={{ width: '580px' }}
                >
                  {shopList.map((shop) => {
                    const {
                      externalId: storeId,
                      name: storeName,
                      bindTask,
                      taskName,
                      alias,
                    } = shop;

                    const storeUsed =
                      bindTask &&
                      initialStores.every((shop) => shop.storeId !== storeId) &&
                      value.every((shop) => shop.storeId !== storeId);

                    return (
                      <Select.Option
                        key={storeId}
                        value={storeId}
                        disabled={disabled || storeUsed}
                        title={storeName}
                      >
                        {storeUsed ? (
                          <UsedShop>
                            <span>
                              <span>{storeName}</span>
                              <Tooltip
                                title={t('aide.该店铺当前适用于任务：taskName', {
                                  taskName,
                                })}
                              >
                                <QuestionCircleOutlined />
                              </Tooltip>
                            </span>
                            <Popconfirm
                              overlayClassName={styles.w300}
                              title={t('aide.您确定要将该店铺由原来的“taskName”修改为此任务吗？', {
                                taskName,
                              })}
                              onConfirm={() => {
                                onChange([
                                  ...value,
                                  {
                                    storeId,
                                    storeName,
                                    alias,
                                  },
                                ]);
                              }}
                            >
                              <Button type="link" size="small">
                                {t('aide.设为适用此任务')}
                              </Button>
                            </Popconfirm>
                          </UsedShop>
                        ) : (
                          storeName
                        )}
                      </Select.Option>
                    );
                  })}
                </Select>
              );
            }}
          </FormField>
        );

        const enableField = (
          <FormField<FV['enable']> name="enable" required label={t('aide.任务开关')}>
            {({ field: { value, onChange } }) => {
              return (
                <Switch
                  disabled={disabled}
                  checked={value}
                  onChange={onChange}
                  checkedChildren={t('aide.开')}
                  unCheckedChildren={t('aide.关')}
                />
              );
            }}
          </FormField>
        );

        const form = (
          <Form>
            {nameField}
            {isNoRegionScenario(scenarioType) ? (
              <StoreScopesField
                disabled={disabled}
                platform={platform}
                permId={permissionNameIdMap[scenarioPermissionMap[scenarioType]]}
              />
            ) : (
              storesField
            )}
            {scenarioType === 'TIMEOUT_REPLY' && (
              <TimeoutReplyField disabled={disabled} platform={platform} />
            )}
            {scenarioType === 'WELCOME_REPLY' && <WelcomeReplyField disabled={disabled} />}
            {enableField}
            <FilterRuleField scenarioType={scenarioType} platform={platform} disabled={disabled} />
            <InviteFollowerField scenarioType={scenarioType} />
            {scenarioType === 'ORDER_REMINDER' && (
              <FilterAfterSaleField
                platform={platform}
                disabled={disabled}
                scenarioType={scenarioType}
              />
            )}
            {scenarioType === 'PRAISE_REMINDER' && <PraiseTriggerPointField disabled={disabled} />}
            <Conflict platform={platform} region={region} scenarioType={scenarioType} />
          </Form>
        );

        return children(form, formProps);
      }}
    </Formik>
  );
}

export default TaskForm;
